/**
 * The script index.html runs before the app: it applies the colour scheme and picks the layout of the loader (full
 * screen, or the login layout on login steps) before the first paint and, while the loader is on screen for two
 * loops, tells the user that loading takes longer than usual and where the status page is. It is served as a
 * classic same-origin script (`bootLoaderVitePlugin.ts`), because CSP allows no inline scripts. It must stay free of
 * imports: it is compiled on its own, without bundling.
 */

export interface BootLoaderData {
  /** One loop of the loader, in ms. */
  loopMs: number
  /** The website of this build's environment (`VITE_AUTH_SIDEBAR_URL`), null when the build has none. */
  websiteUrl: string | null
  /** Per locale: the busy label and the slow message; `text` holds `{link}`. */
  messages: Record<string, { loading: string, title: string, text: string }>
  /** The app's base path (Vite `base`), stripped before the paths below are matched. */
  base: string
  /** Paths that open in the login layout; null for an app without one. */
  loginLayout: BootLoginLayout | null
}

/** Regular expression sources, matched against the path without the base. */
export interface BootLoginLayout {
  /** Login steps that show the login loader (AuthLoader): the loader starts on them right away. */
  loaderPaths: string[]
  /** Login forms: the same layout with the logo still in its place at the top of the column. */
  formPaths: string[]
  /**
   * Without a stored session every other path goes to the login screen (the router guard), so it opens with the
   * login loader too; these paths are public and stay full screen.
   */
  signedOutPublicPaths: string[] | null
}

export type BootLoaderMode = "screen" | "login" | "login-still";

export const APP_READY_CLASS = "app-ready";
/** On <html> when index.html shows the login layout instead of the full screen loader. */
export const LOGIN_LAYOUT_CLASS = "vb-boot-login";
/** On <html> next to LOGIN_LAYOUT_CLASS when the page is a login form: the logo stays still in its place. */
export const LOGIN_STILL_CLASS = "vb-boot-login-still";
/** On <html> next to LOGIN_LAYOUT_CLASS when the page continues the login loader of the previous page. */
export const LOGIN_CONTINUE_CLASS = "vb-boot-login-continue";
/** On <html>: performance.now() when the loader went on screen, for a loader that takes over (authLoaderClock.ts). */
export const LOADER_START_ATTRIBUTE = "data-vb-loader-start";
/** The entry of the login loader (logo to the middle, route, stops) before its first round; 1.3s in authLoader.css. */
export const AUTH_LOADER_ENTRY_MS = 1300;
/** sessionStorage: when (Date.now()) this tab left a page while the login loader was on screen. */
export const LOADER_LEFT_AT_STORAGE_KEY = "vb-login-loader-left-at";
/** The same across origins: the auth server sends `?vb-loader=continue` along when it hands over to the app. */
export const LOADER_CONTINUE_PARAM = "vb-loader";
export const LOADER_CONTINUE_VALUE = "continue";
/** A flag older than this belongs to an earlier visit, not to the step the tab just left. */
export const LOADER_CONTINUE_WITHIN_MS = 10000;
/** The keys the auth store keeps its session in (useAuthStore, useAccountsStore). */
const SESSION_TOKEN_KEY = "token";
const SESSION_REFRESH_TOKEN_KEY = "refresh-token";
const ACTIVE_ACCOUNT_KEY = "active-account";
const AUTHUSER_PARAM = "authuser";
const DEFAULT_LOCALE = "nl";
const FALLBACK_STATUS_PAGE_URL = "https://status.verbleif.com";

/** Same choice as the theme composable: the stored setting, else the system preference. */
export function prefersDark(storedSetting: string | null, systemPrefersDark: boolean): boolean {
  if (storedSetting === "dark") {
    return true;
  }
  if (storedSetting === "light") {
    return false;
  }

  return systemPrefersDark;
}

/** The status page of an environment: `status.` in front of its website host. */
export function statusPageUrl(websiteUrl: string | null): string {
  if (!websiteUrl) {
    return FALLBACK_STATUS_PAGE_URL;
  }

  const website = new URL(websiteUrl);

  return `${website.protocol}//status.${website.hostname.replace(/^www\./, "")}`;
}

/** The website the app will use: a config override from the debug tools wins over the build's own. */
export function activeWebsiteUrl(overrideConfigJson: string | null, buildWebsiteUrl: string | null): string | null {
  if (!overrideConfigJson) {
    return buildWebsiteUrl;
  }

  try {
    const override = JSON.parse(overrideConfigJson) as { authSidebarUrl?: unknown } | null;
    if (override && typeof override.authSidebarUrl === "string" && override.authSidebarUrl !== "") {
      return override.authSidebarUrl;
    }
  } catch {
    // An unreadable override is ignored, like a missing one.
  }

  return buildWebsiteUrl;
}

/** The app's language (`language` in localStorage, else the browser's), when the loader has text for it. */
export function bootLocale(storedLanguage: string | null, navigatorLanguage: string | undefined, available: string[]): string {
  const candidates = [storedLanguage, navigatorLanguage?.split("-")[0]];
  for (const candidate of candidates) {
    const code = candidate?.toLowerCase();
    if (code && available.includes(code)) {
      return code;
    }
  }

  return DEFAULT_LOCALE;
}

type StorageReader = Pick<Storage, "getItem"> | null;

function accountIndex(value: string | null | undefined): number | null {
  return value && /^\d+$/.test(value) ? Number.parseInt(value, 10) : null;
}

/**
 * The session check of the router guard (`hasToken || isRemembered` of useAuthStore), read straight from storage:
 * an access token in this tab, or a remembered refresh token of the tab's account (`?authuser`, else the account
 * of this tab, else the first).
 */
export function hasStoredSession(session: StorageReader, local: StorageReader, search: string): boolean {
  if (session?.getItem(SESSION_TOKEN_KEY) != null || session?.getItem(SESSION_REFRESH_TOKEN_KEY) != null) {
    return true;
  }
  const index = accountIndex(new URLSearchParams(search).get(AUTHUSER_PARAM))
    ?? accountIndex(session?.getItem(ACTIVE_ACCOUNT_KEY))
    ?? 0;

  return local?.getItem(`${SESSION_REFRESH_TOKEN_KEY}.${index}`) != null;
}

/** Whether this page continues the login loader of the page before: a fresh flag of this tab, or the handover parameter. */
export function continuesLoginLoader(leftAt: string | null, now: number, search: string): boolean {
  if (new URLSearchParams(search).get(LOADER_CONTINUE_PARAM) === LOADER_CONTINUE_VALUE) {
    return true;
  }
  const at = Number(leftAt);

  return leftAt !== null && Number.isFinite(at) && now - at >= 0 && now - at <= LOADER_CONTINUE_WITHIN_MS;
}

/** Which loader index.html shows on this path: full screen, the login loader, or the login layout of a form. */
export function bootLoaderMode(pathname: string, base: string, layout: BootLoginLayout | null, signedIn = true): BootLoaderMode {
  if (!layout) {
    return "screen";
  }

  const path = pathname.startsWith(base) ? `/${pathname.slice(base.length)}` : pathname;
  function matches(sources: string[]): boolean {
    return sources.some(source => new RegExp(source).test(path));
  }
  if (matches(layout.loaderPaths)) {
    return "login";
  }
  if (matches(layout.formPaths)) {
    return "login-still";
  }
  if (!signedIn && layout.signedOutPublicPaths && !matches(layout.signedOutPublicPaths)) {
    return "login";
  }

  return "screen";
}

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function applyColorScheme(): void {
  let systemPrefersDark = false;
  try {
    systemPrefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    // matchMedia can throw in restricted contexts; light is the default.
  }

  if (prefersDark(readStorage("themeSetting"), systemPrefersDark)) {
    document.documentElement.classList.add("dark");
  }
}

function safeStorage(kind: "localStorage" | "sessionStorage"): Storage | null {
  try {
    return window[kind];
  } catch {
    return null;
  }
}

/** Reads and clears the flag and the handover parameter: they count for this page load only. */
function takeContinuation(session: Storage | null): boolean {
  let leftAt: string | null = null;
  try {
    leftAt = session?.getItem(LOADER_LEFT_AT_STORAGE_KEY) ?? null;
    session?.removeItem(LOADER_LEFT_AT_STORAGE_KEY);
  } catch {
    // Blocked storage: no flag.
  }
  const search = window.location.search;
  const continues = continuesLoginLoader(leftAt, Date.now(), search);

  const params = new URLSearchParams(search);
  if (params.has(LOADER_CONTINUE_PARAM)) {
    params.delete(LOADER_CONTINUE_PARAM);
    const query = params.toString();
    try {
      window.history.replaceState(window.history.state, "", `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`);
    } catch {
      // The parameter only stays visible in the address bar.
    }
  }

  return continues;
}

function applyMode(data: BootLoaderData): BootLoaderMode {
  const session = safeStorage("sessionStorage");
  let signedIn = true;
  try {
    signedIn = hasStoredSession(session, safeStorage("localStorage"), window.location.search);
  } catch {
    // Unreadable storage: assume a session, the app shows its own loader.
  }
  const mode = bootLoaderMode(window.location.pathname, data.base, data.loginLayout, signedIn);
  const continues = takeContinuation(session);
  const root = document.documentElement;
  root.classList.toggle(LOGIN_LAYOUT_CLASS, mode !== "screen");
  root.classList.toggle(LOGIN_STILL_CLASS, mode === "login-still");
  root.classList.toggle(LOGIN_CONTINUE_CLASS, mode === "login" && continues);
  root.setAttribute(LOADER_START_ATTRIBUTE, String(performance.now()));

  return mode;
}

interface SlowMessageSlot {
  container: string
  titleClass: string
  textClass: string
}

/** Where each layout shows the slow message, and the classes of its title and text. */
const SLOW_MESSAGE_SLOTS: Record<"screen" | "login", SlowMessageSlot> = {
  screen: { container: ".vb-loader-screen__message", titleClass: "vb-loader-screen__title", textClass: "vb-loader-screen__text" },
  login: { container: ".vb-loader-login .auth-loader__slow", titleClass: "auth-loader__slow-title", textClass: "auth-loader__slow-text" },
};

function renderSlowMessage(container: Element, slot: SlowMessageSlot, message: BootLoaderData["messages"][string], url: string): void {
  const title = document.createElement("p");
  title.className = slot.titleClass;
  title.textContent = message.title;

  const text = document.createElement("p");
  text.className = slot.textClass;
  const [before, after = ""] = message.text.split("{link}");
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener";
  link.textContent = new URL(url).host;
  text.append(before, link, after);

  container.replaceChildren(title, text);
}

/** Shows the slow message after two loops of visible loader; the Vue app hides the loader with `app-ready`. */
function watchSlowLoading(data: BootLoaderData, mode: BootLoaderMode): void {
  const root = document.documentElement;
  const locale = bootLocale(readStorage("language"), navigator.language, Object.keys(data.messages));
  const message = data.messages[locale] ?? data.messages[DEFAULT_LOCALE];
  const url = statusPageUrl(activeWebsiteUrl(readStorage("overrideConfig"), data.websiteUrl));
  const slot = SLOW_MESSAGE_SLOTS[mode === "screen" ? "screen" : "login"];
  let timer: number | undefined;

  function container(): Element | null {
    return document.querySelector(slot.container);
  }

  function schedule(alreadyVisibleMs: number): void {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      const target = container();
      if (target && message && !root.classList.contains(APP_READY_CLASS)) {
        renderSlowMessage(target, slot, message, url);
      }
    }, Math.max(0, data.loopMs * 2 - alreadyVisibleMs));
  }

  function onVisibilityChange(): void {
    if (root.classList.contains(APP_READY_CLASS)) {
      window.clearTimeout(timer);
      container()?.replaceChildren();

      return;
    }
    schedule(0);
  }

  function labelScreen(): void {
    const screen = document.querySelector(".vb-loader-screen");
    if (screen && message) {
      screen.setAttribute("aria-label", message.loading);
    }
  }
  // index.html runs this script in <head>, before the loader exists.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", labelScreen);
  } else {
    labelScreen();
  }

  // The loader has been on screen since the page started.
  schedule(performance.now());
  let wasReady = root.classList.contains(APP_READY_CLASS);
  new MutationObserver(() => {
    const isReady = root.classList.contains(APP_READY_CLASS);
    if (isReady !== wasReady) {
      wasReady = isReady;
      onVisibilityChange();
    }
  }).observe(root, { attributes: true, attributeFilter: ["class"] });
}

export function startBootLoader(data: BootLoaderData): void {
  applyColorScheme();
  watchSlowLoading(data, applyMode(data));
}
